export type CheckoutIdentity = { userId: string | null; identityRevision: number };

/** Capture before joining the cart queue. Token refresh is not an identity change.
 * Revision also rejects A -> B -> A and guest -> account -> guest during a wait. */
export function captureCheckoutIdentity(read: () => CheckoutIdentity) {
  const { userId, identityRevision } = read();
  const matches = () => {
    const current = read();
    return current.userId === userId && current.identityRevision === identityRevision;
  };
  return { userId, identityRevision, matches,
    assertCurrent() { if (!matches()) throw new Error("Checkout initiating identity changed"); } };
}

/** Client hint is only a mismatch guard. Authorization remains server context. */
export function checkoutOwnerMatches(initiatingUserId: string | null, authenticatedUserId: string) {
  return initiatingUserId !== null && initiatingUserId === authenticatedUserId;
}

export async function acknowledgeCheckoutIdentity(
  identity: ReturnType<typeof captureCheckoutIdentity>,
  enabled: boolean,
  cartId: string | null,
  invalidate: (owner: string) => void,
  cancel: (input: { cartId: string; initiatingUserId: string }) => Promise<{ checkoutAllowed: boolean; ownerId: string }>,
): Promise<boolean> {
  if (!identity.matches()) return false;
  if (!enabled || !identity.userId) return true;
  if (!cartId) return false;
  invalidate(identity.userId);
  try {
    const result = await cancel({ cartId, initiatingUserId: identity.userId });
    return identity.matches() && result.ownerId === identity.userId && result.checkoutAllowed === true;
  } catch { return false; }
}